
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import BaseCollapse from 'src/components/core/collapse';
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import Type from 'src/components/core/typography/index';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  className?: string,
  disabled?: boolean,
  header?: ReactNode,
  isFaq?: boolean,
  isOpen: boolean,
  onClickOpen?: void,
  scrollOnOpen?: boolean,
  scrollOnOpenPosition?: 'start' | 'center' | 'end' | 'nearest',
  title?: string,
  toggle: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<Pick<Props, 'disabled'>>`
  border-bottom: 1px solid ${color('grey100')};
  position: relative;

  ${ifProp('disabled', css`
    opacity: 0.5;
    pointer-events: none;
  `)}
`;

/**
 * `Header` styled component.
 */

const Header = styled.button<Pick<Props, 'disabled'>>`
  align-items: flex-start;
  appearance: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  justify-content: space-between;
  padding: ${units(3)} ${units(3)} ${units(3)} ${units(1.5)};
  text-align: left;
  transition: opacity ${theme('animations.defaultTransition')};
  width: 100%;

  ${ifProp('disabled', css`
    cursor: default;
  `)}
`;

/**
 * `Arrow` styled component.
 */

const Arrow = styled.span<{ isOpen: boolean }>`
  height: 18px;
  position: relative;
  right: 1px;
  top: ${units(0.5)};
  width: 18px;

  &::before,
  &::after {
    background-color: ${color('black')};
    content: '';
    height: 2px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform-origin: center;
    transition: ${theme('animations.defaultTransition')};
    transition-property: opacity, transform;
    width: 18px;
  }

  &::before {
    transform: translate(-50%, -50%);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(90deg) scaleX(1);
  }

  ${ifProp('isOpen', css`
    &::after {
      transform: translate(-50%, -50%) rotate(90deg) scaleX(0);
    }
  `)}

  &:focus,
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * `StyledBaseCollapse` styled component.
 */

const StyledBaseCollapse = styled(BaseCollapse)`
  position: relative;
`;

/**
 * `Disclosure` component.
 */

const Disclosure = (props: Props): ReactElement => {
  const {
    children,
    disabled,
    header,
    isFaq,
    isOpen,
    scrollOnOpen,
    scrollOnOpenPosition,
    title,
    toggle,
    ...rest
  } = props;

  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    if (disabled && isOpen) {
      toggle();
    }
  }, [disabled, isOpen, toggle]);

  useEffect(() => {
    if (!scrollOnOpen || !isOpen) {
      return;
    }

    const timeout = setTimeout(() => {
      if (ref.current && ref.current.scrollIntoView) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: scrollOnOpenPosition
        });
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [isOpen, scrollOnOpen, scrollOnOpenPosition]);

  return (
    <Wrapper
      disabled={disabled}
      ref={ref}
      {...rest}
      {...(isFaq && {
        itemProp: 'mainEntity',
        itemScope: true,
        itemType: 'https://schema.org/Question'
      })}
    >
      <Header
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={toggle}
        type={'button'}
      >
        {header}

        {title && !header && (
          <Type.Paragraph
            as={'span'}
            {...(isFaq && { itemProp: 'name' })}
          >
            {title}
          </Type.Paragraph>
        )}

        <Arrow isOpen={isOpen} />
      </Header>

      <StyledBaseCollapse isOpen={isOpen}>
        {isFaq ? (
          <div
            itemProp={'acceptedAnswer'}
            itemScope
            itemType={'https://schema.org/Answer'}
          >
            <div itemProp={'text'}>{children}</div>
          </div>
        ) : children
        }
      </StyledBaseCollapse>
    </Wrapper>
  );
};

/**
 * Export `Disclosure` component.
 */

export default Disclosure;
